import { Injectable } from '@angular/core';
import { from, merge, mergeMap, Observable, of, scan, shareReplay } from 'rxjs';
import { filter, map, startWith } from 'rxjs/operators';
import {
  ClientEvent,
  Direction,
  EventType,
  MatrixClient,
  MatrixEvent,
  MatrixEventEvent,
  Room,
  RoomEvent,
  RoomStateEvent,
  SyncState
} from 'matrix-js-sdk';
import { fromMatrixEvent } from '../sync/from-matrix-event';
import { RoomInfo } from '../../model/room-info';
import { RoomHelperService } from '../room/room-helper.service';
import { KnownMembership } from 'matrix-js-sdk/lib/@types/membership.js';
import { RoomPowerLevel } from '../../model/room-power-level';
import { sanitizeMatrixHtml } from '../util';

@Injectable({ providedIn: 'root' })
/**
 * Matrix-Räume mit aktuellem Zustand nach Änderungen durch sendStateEvent.
 * Begründung: Keine AFO (technische Hilfsklasse)
 * 🔸 Unterstützung für Name, Topic
 * 💬 Anzeige der letzten Nachricht (Text, Sender, Zeit)
 * 🕓 Sortierung nach letzter Aktivität
 * Icons für Verschlüsselung & Medientypen
 */
export class MatrixRoomListService {
  getRoomInfos$(client: MatrixClient): Observable<RoomInfo[]> {
    const roomStateEvents$ = this.getRoomStateEvents$(client);

    const newRooms$ = fromMatrixEvent<Room>(client, ClientEvent.Room);

    // Observable-Stream aller Timeline-Events eines Clients.
    // Gibt nur MatrixEvent zurück, ohne Room und toStartOfTimeline.
    const timelineEvents$ = fromMatrixEvent<[MatrixEvent, Room, boolean, boolean, unknown]>(
      client,
      RoomEvent.Timeline
    ).pipe(
      filter(([, room]) => !!room),
      map(([event]) => event)
    );

    const messageEvents$ = timelineEvents$.pipe(
      // Filter: nur gültige Nachrichten
      filter(event => event.getType() === EventType.RoomMessage || event.getType() === EventType.RoomMessageEncrypted),
      mergeMap(event => {
        // Observable für das Event selbst UND seine spätere Entschlüsselung
        // Selbes Event nach Entschlüsselung
        const decrypted$ = fromMatrixEvent<[MatrixEvent]>(event, MatrixEventEvent.Decrypted).pipe(
          map(([event]) => event)
        );

        // Sofort emitten + später erneut, wenn entschlüsselt
        return merge(of(event), decrypted$);
      })
    );

    // Membership-Änderungen, z. B. bei angenommener Einladung
    const memberEvents$ = timelineEvents$.pipe(
      // Filter: nur gültige Nachrichten
      filter(event => event.getType() === EventType.RoomMember),
      filter(event => {
        // Das invite-Event trifft beim Erzeuger des Raums verspätet ein, sodass der Zustand join kurzzeitig wieder auf
        // invite zurückgesetzt wird. Indem wir dieses Event ignorieren, wird während des Zurücksetz-Intervalls kein
        // neuer Snapshot berechnet; der Raum bleibt im Zustand join.
        const creatorsInvite =
          event.getContent().membership === KnownMembership.Invite && event.getSender() === client.getUserId();

        return !event.isRedacted() && !creatorsInvite;
      })
    );

    const deleted$ = fromMatrixEvent<string>(client, ClientEvent.DeleteRoom);

    const receiptEvents$ = fromMatrixEvent(client, RoomEvent.Receipt);

    // Sync-Status-Wechsel (z. B. nach erneutem Einladen): erneut Snapshot berechnen,
    // auch wenn kein passendes Timeline-/Member-Event durchläuft.
    const sync$ = fromMatrixEvent<[SyncState]>(client, ClientEvent.Sync).pipe(
      map(([state]) => state),
      filter(state => state === SyncState.Syncing || state === SyncState.Prepared)
    );

    return merge(roomStateEvents$, messageEvents$, memberEvents$, newRooms$, deleted$, receiptEvents$, sync$).pipe(
      startWith(null),
      map(event => {
        const snapshot = this.getAllRoomsSnapshot(client);
        if (event instanceof MatrixEvent) {
          this.applyEventToSnapshot(snapshot, event);
        }
        return snapshot.sort((a, b) => b.ts - a.ts);
      }),
      /*
      // TODO Funktioniert so nicht mit später eintreffenden decrypted messages
      distinctUntilChanged(
        (a, b) =>
          a.length === b.length &&
          a.every(
            (r, i) =>
              r.roomId === b[i].roomId &&
              r.ts === b[i].ts &&
              r.tsMember === b[i].tsMember
          )
      ),
      */
      shareReplay({ bufferSize: 1, refCount: true })
    );
  }

  private getAllRoomsSnapshot(client: MatrixClient): RoomInfo[] {
    const myId = client.getUserId();
    return client.getRooms().map(room => this.toRoomInfo(room, myId));
  }

  /** Wendet ein einzelnes Live-Event auf den passenden RoomInfo im Snapshot an (mutierend). */
  private applyEventToSnapshot(snapshot: RoomInfo[], event: MatrixEvent): void {
    const currRoomInfo = snapshot.find(r => r.roomId === event.getRoomId());
    if (!currRoomInfo) {
      return;
    }

    const type = event.getType();
    const isMessage =
      type === EventType.RoomMessage || type === EventType.RoomMessageEncrypted || type === MatrixEventEvent.Decrypted;

    if (isMessage) {
      this.applyMessageEvent(currRoomInfo, event);
    } else if (type === EventType.RoomMember && currRoomInfo.myMembership === 'invite') {
      this.applyMemberEvent(currRoomInfo, event);
    }
  }

  private applyMessageEvent(currRoomInfo: RoomInfo, event: MatrixEvent): void {
    const ts = event.getTs();
    if (ts <= currRoomInfo.ts) {
      return;
    }

    currRoomInfo.ts = ts;
    const body = this.getMessageBody(event);
    if (body && body.trim().length > 0) {
      currRoomInfo.lastMessage = { sender: event.getSender() ?? '', body, ts };
    }
  }

  private applyMemberEvent(currRoomInfo: RoomInfo, event: MatrixEvent): void {
    // Timestamp nicht vergleichen - jedes Event zum Update nutzen!
    currRoomInfo.tsMember = event.getTs();
    const membership = event.getContent().membership;
    if (membership) {
      currRoomInfo.myMembership = membership;
    }
  }

  private toRoomInfo(room: Room, myUserId: string | null): RoomInfo {
    const state = room.getLiveTimeline().getState(Direction.Forward);
    const [unreadMessageCount, isMentioned] = myUserId ? this.countUnreadAndMention(room, myUserId) : [0, false];
    const nameEvent = this.getLatestStateEvent(room, EventType.RoomName);
    const topicEvent = this.getLatestStateEvent(room, EventType.RoomTopic);
    const powerLevelEvent = state?.getStateEvents(EventType.RoomPowerLevels, '');

    const name = nameEvent?.getContent()?.['name'] ?? room.name ?? null;
    const topic = topicEvent?.getContent()?.['topic'] ?? null;
    const powerLevel = (powerLevelEvent?.getContent() as RoomPowerLevel) ?? null;

    const lastEvent = this.getLastMessageEvent(room);
    const lastMessage = lastEvent
      ? {
          sender: lastEvent.getSender?.() ?? '',
          body: this.getMessageBody(lastEvent),
          ts: lastEvent.getTs()
        }
      : null;

    const ts = Math.max(
      lastMessage?.ts ?? 0,
      room.getLastActiveTimestamp?.() ?? 0,
      nameEvent?.getTs() ?? 0,
      topicEvent?.getTs() ?? 0
    );

    const tsMember = this.getLastMemberEvent(room)?.getTs() ?? 0;

    // Überschreibe name und theme/topic mit aktuellen Werten aus Timeline.
    // Room ist evtl. noch nicht auf dem aktuellen Stand (synchronisiert).
    const apiRoom = {
      ...RoomHelperService.toApiRoom(room),
      name,
      theme: topic
    };

    const modelRoom = {
      ...RoomHelperService.toModelRoom(room),
      name,
      theme: topic
    };

    const myMembership = room.getMyMembership();

    return {
      roomId: room.roomId,
      name,
      topic,
      ts,
      unreadMessageCount,
      isMentioned,
      lastMessage,
      apiRoom,
      modelRoom,
      myMembership,
      tsMember,
      powerLevel
    };
  }

  /**
   * Ermittelt das neueste gültige State-Event aus State und Live-Timeline.
   */
  private getLatestStateEvent(room: Room, type: EventType.RoomName | EventType.RoomTopic): MatrixEvent | null {
    const stateEvent = room.getLiveTimeline().getState(Direction.Forward)?.getStateEvents(type, '');
    const candidates = [
      ...(stateEvent && !stateEvent.isRedacted() ? [stateEvent] : []),
      ...(room.getLiveTimeline().getEvents() ?? []).filter(
        event => event.getType() === type && !event.isRedacted() && event.getStateKey() === ''
      )
    ];

    return candidates.reduce<MatrixEvent | null>(
      (latest, event) => (!latest || event.getTs() > latest.getTs() ? event : latest),
      null
    );
  }

  private getLastMessageEvent(room: Room): MatrixEvent | null {
    const timeline = room.getLiveTimeline();
    const events = timeline?.getEvents() ?? [];
    for (let i = events.length - 1; i >= 0; i--) {
      const ev = events[i];
      const type = ev.getType();
      if ((type === EventType.RoomMessage || type === EventType.RoomMessageEncrypted) && !ev.isRedacted()) {
        return ev;
      }
    }
    return null;
  }

  private getLastMemberEvent(room: Room): MatrixEvent | null {
    const timeline = room.getLiveTimeline();
    const events = timeline?.getEvents() ?? [];
    for (let i = events.length - 1; i >= 0; i--) {
      const ev = events[i];
      const type = ev.getType();
      if (type === EventType.RoomMember && !ev.isRedacted()) {
        return ev;
      }
    }
    return null;
  }

  /** 🧩 Nachrichtentext — inkl. Media-Typen */
  private getMessageBody(ev: MatrixEvent): string {
    const content = ev.getContent?.();
    if (!content) return '[unbekannte Nachricht]';

    const msgtype = content['msgtype'];
    let body = content['m.new_content']
      ? content['m.new_content']['formatted_body'] || content['m.new_content']['body']
      : content['formatted_body'] || content['body'];

    body = sanitizeMatrixHtml(body);

    switch (msgtype) {
      case 'm.text':
        return body ?? '';
      case 'm.emote':
        return `* ${body ?? ''}`;
      case 'm.notice':
        return `[Notice] ${body ?? ''}`;
      case 'm.image':
        return '📷 Bild';
      case 'm.video':
        return '🎬 Video';
      case 'm.audio':
        return '🎵 Audio';
      case 'm.file':
        return '📎 Datei';
      case 'm.sticker':
        return '🩷 Sticker';
      case 'm.location':
        return '📍 Location';
    }

    if (ev.getType?.() === EventType.RoomMessageEncrypted) {
      if (ev.isBeingDecrypted?.()) return '🔒 Entschlüsselung läuft…';
      if (ev.isDecryptionFailure?.()) return '❌ Entschlüsselung fehlgeschlagen';
      return '🔐 Verschlüsselte Nachricht';
    }

    return '[Unbekannter Nachrichtentyp]';
  }

  /********************************************************************************************************************
   * RoomStateEvents
   ********************************************************************************************************************/

  private getRoomStateEvents$(client: MatrixClient): Observable<MatrixEvent> {
    // 1. Existierende Räume beim Start
    const existingRooms$ = from(client.getRooms()).pipe(mergeMap(room => this.createRoomStateStream(room)));

    // 2. Neue Räume, die während der Laufzeit entstehen
    const newRooms$ = fromMatrixEvent<Room>(client, ClientEvent.Room).pipe(
      mergeMap(room => this.createRoomStateStream(room))
    );

    // 3. Alles zusammenführen
    return merge(existingRooms$, newRooms$).pipe(
      filter(
        event =>
          event.getType() === EventType.RoomName ||
          event.getType() === EventType.RoomTopic ||
          event.getType() === EventType.RoomPinnedEvents
      ),
      // Gleiche Timestamp ab mit größtem Timestamp aller vorherigen Events desselben Typs.
      // Use Case: Manchmal werden mehrere Events desselben Typs empfangen mit unterschiedlichem Inhalt, aber in der
      // falschen Reihenfolge (Race Condition!), d. h., erst neu, dann alt → Filtere 2. Event raus.
      scan(
        (acc, event) => {
          const ts = event.getTs();
          switch (event.getType()) {
            case EventType.RoomName:
              return ts > acc.lastNameTs ? { ...acc, lastNameTs: ts, event } : { ...acc, event: null };
            case EventType.RoomTopic:
              return ts > acc.lastTopicTs ? { ...acc, lastTopicTs: ts, event } : { ...acc, event: null };
            case EventType.RoomPinnedEvents:
              return ts > acc.lastPinnedTs ? { ...acc, lastPinnedTs: ts, event } : { ...acc, event: null };
            default: // Never reached
              return acc;
          }
        },
        {
          lastNameTs: 0,
          lastTopicTs: 0,
          lastPinnedTs: 0,
          event: null as MatrixEvent | null
        }
      ),
      // Nur Events emittieren, die nicht gefiltert wurden aufgrund eines veralteten Timestamps
      filter(acc => !!acc.event),
      map(acc => acc.event as MatrixEvent)
    );
  }

  /**
   * Baut den Stream für RoomState.events in einem einzelnen Raum.
   */
  private createRoomStateStream(room: Room): Observable<MatrixEvent> {
    return fromMatrixEvent<[MatrixEvent, any]>(room, RoomStateEvent.Events).pipe(map(([event]) => event));
  }

  /**
   * Errechnet die Anzahl nicht gelesener Nachrichten im Raum
   * @param room
   * @param myUserId
   * @private
   */
  private countUnreadAndMention(room: Room, myUserId: string): [number, boolean] {
    const eventReadUpTo = room.getEventReadUpTo(myUserId);
    let unreadCounter = 0;
    let isMentioned = false;
    const events = room.getLiveTimeline().getEvents();

    for (let i = events.length - 1; i >= 0; i--) {
      if (events[i].getId() === eventReadUpTo) {
        return [unreadCounter, isMentioned];
      }

      if (this.isMessageFromAnotherUser(events[i], myUserId)) {
        unreadCounter++;
      }
      if (!isMentioned) {
        isMentioned = this.hasMention(events[i], myUserId);
      }
    }
    return [unreadCounter, isMentioned];
  }

  private isMessageFromAnotherUser(event: MatrixEvent, myUserId: string): boolean {
    return event.getType() === EventType.RoomMessage && !!event.sender && event.sender?.userId !== myUserId;
  }

  private hasMention(event: MatrixEvent, myUserId: string): boolean {
    if (event.getType() === EventType.RoomMessage) {
      const mentions = event.getContent()?.['m.mentions'];

      if (mentions && event.getSender() !== myUserId) {
        if (mentions.user_ids?.includes(myUserId)) {
          return true;
        }
        if (mentions?.['room'] === true) {
          return true;
        }
      }
    }
    return false;
  }
}
